import React from "react";
import { Link } from "react-router-dom";
import { Clock, Zap } from "lucide-react";

// Localised copy — 5 languages the app already supports.
const TR = {
  sk: {
    daysLeft: (n) => `Ešte ${n} ${n === 1 ? "deň" : (n >= 2 && n <= 4) ? "dni" : "dní"}`,
    lastDay: "Posledný deň prístupu!",
    urgentTitle: "Tvoje predplatné čoskoro vyprší",
    softTitle: "Blíži sa koniec predplatného",
    urgentDesc: "Predĺž teraz, aby si o prístup neprišiel/neprišla.",
    softDesc: "Predlžením teraz si zachováš plynulý prístup ku všetkým funkciám.",
    cta: "Predĺž teraz",
    until: "Platné do",
  },
  en: {
    daysLeft: (n) => `${n} day${n === 1 ? "" : "s"} left`,
    lastDay: "Last day of access!",
    urgentTitle: "Your subscription expires soon",
    softTitle: "Subscription is running out",
    urgentDesc: "Renew now so you don't lose access.",
    softDesc: "Renew now to keep uninterrupted access to all features.",
    cta: "Renew now",
    until: "Valid until",
  },
  cs: {
    daysLeft: (n) => `Ještě ${n} ${n === 1 ? "den" : (n >= 2 && n <= 4) ? "dny" : "dní"}`,
    lastDay: "Poslední den přístupu!",
    urgentTitle: "Tvé předplatné brzy vyprší",
    softTitle: "Blíží se konec předplatného",
    urgentDesc: "Prodluž nyní, ať o přístup nepřijdeš.",
    softDesc: "Prodloužením si zachováš plynulý přístup ke všem funkcím.",
    cta: "Prodloužit",
    until: "Platné do",
  },
  de: {
    daysLeft: (n) => `Noch ${n} Tag${n === 1 ? "" : "e"}`,
    lastDay: "Letzter Zugriffstag!",
    urgentTitle: "Dein Abo läuft bald ab",
    softTitle: "Abo läuft aus",
    urgentDesc: "Jetzt verlängern, damit du keinen Zugriff verlierst.",
    softDesc: "Verlängere jetzt für unterbrechungsfreien Zugriff.",
    cta: "Jetzt verlängern",
    until: "Gültig bis",
  },
  pl: {
    daysLeft: (n) => `Zostało ${n} ${n === 1 ? "dzień" : (n >= 2 && n <= 4) ? "dni" : "dni"}`,
    lastDay: "Ostatni dzień dostępu!",
    urgentTitle: "Twoja subskrypcja wkrótce wygaśnie",
    softTitle: "Zbliża się koniec subskrypcji",
    urgentDesc: "Przedłuż teraz, żeby nie stracić dostępu.",
    softDesc: "Przedłuż teraz, aby zachować nieprzerwany dostęp.",
    cta: "Przedłuż teraz",
    until: "Ważne do",
  },
  fr: {
    daysLeft: (n) => `Encore ${n} ${n === 1 ? "jour" : "jours"}`,
    lastDay: "Dernier jour d'accès !",
    urgentTitle: "Ton abonnement expire bientôt",
    softTitle: "Fin d'abonnement proche",
    urgentDesc: "Renouvelle maintenant pour ne pas perdre l'accès.",
    softDesc: "En renouvelant maintenant, tu gardes un accès continu à toutes les fonctionnalités.",
    cta: "Renouveler maintenant",
    until: "Valable jusqu'au",
  },
};

/**
 * Shows a color-coded expiry banner in the user's Profile when their paid
 * subscription is within its final week. Colour scheme:
 *   ≤ 7 days → amber/yellow (soft warning)
 *   ≤ 2 days → red (urgent — includes today = 1 day left)
 * Hidden entirely when: user free, > 7 days remaining, tier is "excellent"
 * (lifetime), or subscription_until missing.
 */
export default function PlanExpiryBanner({ user, lang = "sk", subInfo }) {
  const tr = TR[lang] || TR.sk;
  if (!user || !user.is_subscribed) return null;
  if (user.plan_tier === "excellent") return null;    // lifetime VIP — no banner
  const untilIso = user.subscription_until;
  if (!untilIso) return null;

  let untilDate;
  try {
    untilDate = new Date(untilIso);
    if (isNaN(untilDate.getTime())) return null;
  } catch {
    return null;
  }

  const now = new Date();
  const msLeft = untilDate.getTime() - now.getTime();
  const daysLeft = Math.ceil(msLeft / (1000 * 60 * 60 * 24));

  // Already expired or more than a week left → banner hidden (other UI handles those).
  if (daysLeft < 0 || daysLeft > 7) return null;

  // Also skip when subscription is set to auto-renew and NOT cancelled — user
  // will be billed automatically, no urgency to show a renew CTA.
  const isAutoRenewSafe = subInfo?.is_recurring && !subInfo?.cancel_at_period_end;
  if (isAutoRenewSafe) return null;

  const urgent = daysLeft <= 2;
  const border = urgent ? "border-red-700" : "border-amber-700";
  const bg = urgent ? "bg-red-950/30" : "bg-amber-950/25";
  const iconCol = urgent ? "text-red-400" : "text-amber-400";
  const badgeCol = urgent
    ? "!text-red-300 !border-red-700 bg-red-950/40"
    : "!text-amber-300 !border-amber-700 bg-amber-950/40";
  const ctaCls = urgent
    ? "wb-btn-primary !bg-red-600 hover:!bg-red-500 !border-red-500"
    : "wb-btn-primary !bg-amber-600 hover:!bg-amber-500 !border-amber-500";

  const label = daysLeft <= 1 ? tr.lastDay : tr.daysLeft(daysLeft);
  const title = urgent ? tr.urgentTitle : tr.softTitle;
  const desc = urgent ? tr.urgentDesc : tr.softDesc;

  return (
    <div
      data-testid="plan-expiry-banner"
      className={`border ${border} ${bg} p-5 mb-6 flex flex-col sm:flex-row gap-4 items-start sm:items-center`}
    >
      <div className={`flex-shrink-0 w-12 h-12 border ${border} ${bg} flex items-center justify-center`}>
        <Clock className={`w-6 h-6 ${iconCol}`} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2 flex-wrap mb-1">
          <div className="font-display font-bold text-base text-white">{title}</div>
          <span
            data-testid="plan-expiry-days"
            className={`wb-tag ${badgeCol} !text-[10px] tracking-widest`}
          >
            {label.toUpperCase()}
          </span>
        </div>
        <div className="text-xs text-zinc-400 leading-relaxed">
          {desc}
          {" "}
          <span className="text-zinc-500">
            ({tr.until}: {untilDate.toLocaleDateString()})
          </span>
        </div>
      </div>
      <Link
        to="/pricing"
        data-testid="plan-expiry-cta"
        className={`${ctaCls} px-6 py-2.5 text-xs uppercase tracking-widest inline-flex items-center gap-2 flex-shrink-0`}
      >
        <Zap className="w-4 h-4" />
        {tr.cta}
      </Link>
    </div>
  );
}
